// src/hooks/use-onboarding-storage.ts
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useInitializeOnboardingMutation,
  useGetOnboardingProgressQuery,
  useUpdateOnboardingMutation,
  useCompleteOnboardingMutation,
  useDismissOnboardingMutation
} from '@/services/businessOnboardingApi';

/*
 * TODO: ARCHITECTURAL DECISION - SEPARATED ONBOARDING STORAGE SYSTEMS
 * 
 * Developer Note: We intentionally maintain TWO separate storage systems for onboarding:
 * 
 * 1. GUIDED WALKTHROUGH STORAGE (this hook):
 *    - Key: `staffluent_onboarding_${businessId}`
 *    - Purpose: Controls the interactive tooltip-based walkthrough that guides users through the UI
 *    - Data: { isActive, currentStep, isComplete, completionCount }
 *    - Used by: Sidebar component and OnboardingTooltip
 * 
 * 2. BUSINESS SETUP PROGRESS STORAGE (local component state):
 *    - Key: `staffluent_setup_progress_${businessId}`
 *    - Purpose: Tracks actual completion status of business setup tasks
 *    - Data: { completedSteps: ['welcome', 'departments', ...], lastUpdated }
 *    - Used by: Business Onboarding page component
 * 
 * BACKEND INTEGRATION: API calls are "fire and forget" - they run in background
 * without blocking the UI or changing the localStorage-first approach.
 * 
 * ACCOUNT TYPE RESTRICTION: Onboarding only runs for 'business' account types.
 * Other account types receive inactive/complete state immediately.
 */

export const useOnboardingStorage = (businessId: string) => {
  const navigate = useNavigate();
  
  // Check account type first - onboarding only for business accounts
  const accountType = localStorage.getItem('accountType');

  // PRIMARY STATE - localStorage is source of truth for immediate UI
  const [isComplete, setIsComplete] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [completionCount, setCompletionCount] = useState(0);
  
  const storageKey = `staffluent_onboarding_${businessId}`;

  // BACKGROUND API HOOKS - Fire and forget only
  const [initializeOnboarding] = useInitializeOnboardingMutation();
  const [updateOnboarding] = useUpdateOnboardingMutation();
  const [completeOnboardingMutation] = useCompleteOnboardingMutation();
  const [dismissOnboardingMutation] = useDismissOnboardingMutation();

  // Get API data but don't wait for it or block UI - only for business accounts
  const { data: walkthroughData } = useGetOnboardingProgressQuery({
    businessId,
    type: 'walkthrough'
  }, {
    skip: !businessId || accountType !== 'business'
  });

  // INITIALIZE ONBOARDING STATE - handles both business and non-business accounts
  useEffect(() => {
    // Handle non-business accounts - set inactive/complete state
    if (accountType !== 'business') {
      setIsActive(false);
      setIsComplete(true);
      setCurrentStep(0);
      setCompletionCount(999); // High number to indicate permanently dismissed
      return;
    }

    // Check if mobile or PWA - don't initialize onboarding
    const isMobile = window.innerWidth < 768;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                  (window.navigator as any).standalone === true;

    if (isMobile || isPWA) {
      setIsActive(false);
      setIsComplete(true);
      setCompletionCount(999);
      return;
    }

    // Load from localStorage immediately - no API dependency
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const data = JSON.parse(stored);
        setIsComplete(data.isComplete || false);
        setCurrentStep(data.currentStep || 0);
        setIsActive(data.isActive || false);
        setCompletionCount(data.completionCount || 0);
      } catch (error) {
        // Default to first time user if parsing fails
        setIsActive(true);
        setCurrentStep(0);
        setIsComplete(false);
        setCompletionCount(0);
        if (window.location.pathname !== '/') {
          navigate('/');
        }
      }
    } else {
      // First time - auto start (desktop only, business accounts only)
      setIsActive(true);
      setCurrentStep(0);
      setIsComplete(false);
      setCompletionCount(0);
      if (window.location.pathname !== '/') {
        navigate('/');
      }
    }

    // BACKGROUND API SYNC - fire and forget, don't wait
    if (businessId && !isMobile && !isPWA) {
      initializeOnboarding({
        businessId,
        type: 'walkthrough',
        deviceType: getDeviceType(),
        userAgent: navigator.userAgent,
        isPWA: isPWA,
        isFirstTime: !stored
      }).catch(() => {
        // Silent fail - don't impact user experience
      });
    }
  }, [businessId, accountType, navigate]);

  const saveProgress = (step: number, complete: boolean = false) => {
    // Skip for non-business accounts
    if (accountType !== 'business') {
      return;
    }

    const newCompletionCount = complete ? completionCount + 1 : completionCount;
    
    // UPDATE UI STATE IMMEDIATELY - 0 delay
    const newData = { 
      currentStep: step, 
      isComplete: complete,
      isActive: !complete,
      completionCount: newCompletionCount
    };
    
    // Update state immediately
    setCurrentStep(step);
    setIsComplete(complete);
    setIsActive(!complete);
    setCompletionCount(newCompletionCount);
    
    // Save to localStorage immediately
    localStorage.setItem(storageKey, JSON.stringify(newData));

    // BACKGROUND API SYNC - fire and forget
    if (walkthroughData?.id) {
      updateOnboarding({
        id: walkthroughData.id,
        currentStep: step,
        status: complete ? 'completed' : step > 0 ? 'in_progress' : 'not_started'
      }).catch(() => {
        // Silent fail - don't impact user experience
      });
    }
  };

  const startOnboarding = () => {
    // Skip for non-business accounts
    if (accountType !== 'business') {
      return;
    }

    // UPDATE UI IMMEDIATELY
    setIsActive(true);
    setCurrentStep(0);
    setIsComplete(false);

    const newData = { 
      currentStep: 0, 
      isComplete: false,
      isActive: true,
      completionCount: completionCount
    };
    
    // Save to localStorage immediately
    localStorage.setItem(storageKey, JSON.stringify(newData));

    // BACKGROUND API SYNC - fire and forget
    if (walkthroughData?.id) {
      updateOnboarding({
        id: walkthroughData.id,
        currentStep: 0,
        status: 'in_progress'
      }).catch(() => {});
    } else {
      initializeOnboarding({
        businessId,
        type: 'walkthrough',
        deviceType: getDeviceType(),
        userAgent: navigator.userAgent,
        isPWA: window.matchMedia('(display-mode: standalone)').matches,
        isFirstTime: false
      }).catch(() => {});
    }

    // Force page reload to ensure fresh state
    window.location.reload();
  };

  const completeOnboarding = () => {
    // Skip for non-business accounts
    if (accountType !== 'business') {
      return;
    }

    // UPDATE UI IMMEDIATELY
    setIsActive(false);
    const newData = { 
      currentStep: 0, 
      isComplete: true,
      isActive: false,
      completionCount: completionCount + 1
    };
    
    setIsComplete(true);
    setCompletionCount(completionCount + 1);
    
    // Save to localStorage immediately
    localStorage.setItem(storageKey, JSON.stringify(newData));

    // BACKGROUND API SYNC - fire and forget
    completeOnboardingMutation({
      businessId,
      type: 'walkthrough'
    }).catch(() => {
      // Silent fail - don't impact user experience
    });
  };

  const dismissPermanently = () => {
    // Skip for non-business accounts
    if (accountType !== 'business') {
      return;
    }

    // UPDATE UI IMMEDIATELY
    const newData = { 
      currentStep: 0, 
      isComplete: true,
      isActive: false,
      completionCount: 999
    };
    
    setIsComplete(true);
    setIsActive(false);
    setCompletionCount(999);
    
    // Save to localStorage immediately
    localStorage.setItem(storageKey, JSON.stringify(newData));

    // BACKGROUND API SYNC - fire and forget
    dismissOnboardingMutation({
      businessId,
      type: 'walkthrough',
      reason: 'user_dismissed'
    }).catch(() => {
      // Silent fail - don't impact user experience
    });
  };

  const getDeviceType = (): string => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  return {
    isComplete,
    currentStep,
    isActive,
    completionCount,
    setIsActive: (active: boolean) => {
      // Only allow setting active state for business accounts
      if (accountType === 'business') {
        setIsActive(active);
      }
    },
    saveProgress,
    startOnboarding,
    completeOnboarding,
    dismissPermanently
  };
};